import React, { useCallback, useMemo, useRef, useState } from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Table from '@/components/General/Table/Table';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import CheckinDetailsModal from '@/app/(tabs)/(home,search,notifications,settings)/events/instances/[eventInstanceID]/_checkin/CheckinDetailsModal';

const CheckinTableView = ({
    data,
    isLoading,
    onPersonPress,
    onFamilyPress,
    onCheckOut,
    onEndReached,
    onRefresh,
}) => {
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedPersonId, setSelectedPersonId] = useState(null);

    const onFamilyPressRef = useRef(onFamilyPress);
    onFamilyPressRef.current = onFamilyPress;

    const onPersonPressRef = useRef(onPersonPress);
    onPersonPressRef.current = onPersonPress;

    const onCheckOutRef = useRef(onCheckOut);
    onCheckOutRef.current = onCheckOut;

    const selectedPerson = useMemo(() => {
        return data.find(p => p.id === selectedPersonId) || null;
    }, [data, selectedPersonId]);

    const handleDetailsPress = useCallback((person) => {
        setSelectedPersonId(person.id);
        setDetailsModalOpen(true);
    }, []);

    const handleModalClose = useCallback(() => {
        setDetailsModalOpen(false);
        setSelectedPersonId(null);
    }, []);

    const handleRowClick = useCallback((item) => {
        onPersonPressRef.current?.(item);
    }, []);

    const columns = useMemo(() => [
        {
            value: 'Name',
            accessor: 'name',
            minWidth: 150,
            renderer: (item) => (
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Avatar size={32} profile={item} disableBorder />
                    <View style={[styles.cellContainer, { marginLeft: 8 }]}>
                        <Text style={styles.nameText}>{item.firstName} {item.lastName}</Text>
                        {item.subtitle && <Text style={styles.subtitleText} numberOfLines={1}>{item.subtitle}</Text>}
                    </View>
                </View>
            )
        },
        {
            value: 'Status',
            accessor: 'status',
            minWidth: 120,
            renderer: (item) => (
                <View style={styles.cellContainer}>
                    {item.isProcessing ? (
                        <View style={styles.statusBadge('default')}>
                            <ActivityIndicator size={14} />
                            <Text style={styles.statusText('default')}>Working...</Text>
                        </View>
                    ) : item.isSelected ? (
                        <View style={styles.statusBadge('success')}>
                            <Unicon name={item.isCheckedOut ? "exit" : "checkmark-circle"} size={14} colorVariant="success" />
                            <Text style={styles.statusText('success')}>
                                {item.isCheckedOut ? 'Checked Out' : 'Checked In'}
                            </Text>
                        </View>
                    ) : item.isPending ? (
                        <View style={styles.statusBadge('warning')}>
                            <Unicon name="time" size={14} colorVariant="warning" />
                            <Text style={styles.statusText('warning')}>Pending</Text>
                        </View>
                    ) : (
                        <View style={styles.statusBadge('default')}>
                            <Unicon name="ellipse-outline" size={14} colorVariant="textSecondary" />
                            <Text style={styles.statusText('default')}>Not Checked In</Text>
                        </View>
                    )}
                </View>
            )
        },
        {
            value: 'Family',
            accessor: 'family',
            minWidth: 200,
            renderer: (item) => {
                const familyMembers = item?.Family?.FamilyMembers?.filter(p => p.id != item?.id) || [];
                if (familyMembers.length === 0) return <View style={styles.cellContainer}><Text style={styles.emptyText}>—</Text></View>;

                return (
                    <View style={styles.familyAvatarsWrap}>
                        {familyMembers.map(member => (
                            <View key={member.id} style={styles.familyAvatarWrapper}>
                                <Avatar
                                    size={30}
                                    profile={member}
                                    disableBorder
                                    onPress={() => onFamilyPressRef.current?.(member, item)}
                                    tooltipExtraText={member.selectionDetail}
                                />
                                {(member.isSelected || member.isPending) && (
                                    <View style={styles.familyAvatarBadge}>
                                        <Unicon
                                            name={member.isCheckedOut ? "exit" : "checkmark-circle"}
                                            size={12}
                                            colorVariant={member.isPending ? "warning" : "success"}
                                        />
                                    </View>
                                )}
                            </View>
                        ))}
                    </View>
                );
            }
        },
        {
            value: 'Other Household Members',
            accessor: 'otherHousehold',
            minWidth: 200,
            renderer: (item) => {
                const familyIds = new Set((item?.Family?.FamilyMembers || []).map(p => p.id));
                const otherHouseholdMembers = item?.Address?.People?.filter(p => p.id != item?.id && !familyIds.has(p.id)) || [];
                if (otherHouseholdMembers.length === 0) return <View style={styles.cellContainer}><Text style={styles.emptyText}>—</Text></View>;

                return (
                    <View style={styles.familyAvatarsWrap}>
                        {otherHouseholdMembers.map(member => (
                            <View key={member.id} style={styles.familyAvatarWrapper}>
                                <Avatar
                                    size={30}
                                    profile={member}
                                    disableBorder
                                    onPress={() => onFamilyPressRef.current?.(member, item)}
                                    tooltipExtraText={member.selectionDetail}
                                />
                                {(member.isSelected || member.isPending) && (
                                    <View style={styles.familyAvatarBadge}>
                                        <Unicon
                                            name={member.isCheckedOut ? "exit" : "checkmark-circle"}
                                            size={12}
                                            colorVariant={member.isPending ? "warning" : "success"}
                                        />
                                    </View>
                                )}
                            </View>
                        ))}
                    </View>
                );
            }
        },
        {
            value: 'Actions',
            accessor: 'actions',
            minWidth: 250,
            renderer: (item) => (
                <View style={[styles.cellContainer, styles.actionsCell]}>
                    {item.isSelected && !item.isCheckedOut && !item.isPending && !item.isProcessing ? (
                        <SolidButton
                            text="Check Out"
                            iconName="exit"
                            iconBefore
                            size="sm"
                            type="primary"
                            onPress={() => onCheckOutRef.current?.(item)}
                        />
                    ) : null}
                    <SolidButton
                        text="View Details"
                        iconName="information-circle"
                        iconBefore
                        size="sm"
                        type="secondary"
                        onPress={() => handleDetailsPress(item)}
                    />
                </View>
            )
        },
    ], []);

    return (
        <>
            <Table
                columns={columns}
                data={data}
                isLoading={isLoading}
                onRowClick={handleRowClick}
                onEndReached={onEndReached}
                onRefresh={onRefresh}
                estimatedItemSize={50}
            />
            <CheckinDetailsModal
                isOpen={detailsModalOpen}
                onClose={handleModalClose}
                person={selectedPerson}
                onCheckIn={onPersonPress}
                onCheckOut={onCheckOut}
            />
        </>
    );
};

const styles = StyleSheet.create(({colors, fonts}) => ({
    cellContainer: {
        justifyContent: 'center',
        paddingVertical: 10,
        minHeight: 50,
    },
    actionsCell: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
    },
    nameText: {
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    subtitleText: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        marginTop: 2,
    },
    statusBadge: (variant) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 4,
        paddingHorizontal: 8,
        borderRadius: 12,
        backgroundColor: variant === 'success' ? colors.successContainer 
            : variant === 'info' ? colors.infoContainer 
            : variant === 'warning' ? colors.warningContainer 
            : colors.surface,
        alignSelf: 'flex-start',
    }),
    statusText: (variant) => ({
        fontSize: 12,
        fontFamily: fonts.body,
        color: variant === 'success' ? colors.success 
            : variant === 'info' ? colors.info 
            : variant === 'warning' ? colors.warning 
            : colors.textSecondary,
    }),
    emptyText: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        paddingVertical: 10,
    },
    familyAvatarsWrap: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: 6,
        paddingVertical: 8,
        minHeight: 50,
    },
    familyAvatarWrapper: {
        position: 'relative',
    },
    familyAvatarBadge: {
        position: 'absolute',
        bottom: -2,
        right: -2,
    },
}));

export default React.memo(CheckinTableView);
